import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  Search,
  ArrowRight,
  Shield,
  MessageCircle,
  Heart,
  Check,
  Building2,
} from "lucide-react";
import { plans } from "../mock";
import { BookConsultationDialog } from "../components/BookConsultationDialog";

const letters = [
  { letter: "H", word: "Human", desc: "Real people showing up for someone they love." },
  { letter: "A", word: "Assistance", desc: "Tangible help — meals, rides, errands, presence." },
  { letter: "R", word: "Response", desc: "Quick, organized, and pointed where it's needed most." },
  { letter: "T", word: "Team", desc: "Family, friends, and care team — moving as one." },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [bookingPlan, setBookingPlan] = useState(null);

  const handleSearch = (e) => {
    e.preventDefault();
    const slug = search.trim().toLowerCase().replace(/\s+/g, "-");
    if (slug) navigate(`/${slug}`);
  };

  return (
    <main className="min-h-screen bg-gradient-warm">
      {/* Hero */}
      <section className="mx-auto max-w-3xl px-6 pt-20 pb-16 text-center animate-fade-up">
        <div className="inline-flex items-center gap-2 rounded-full bg-card/70 backdrop-blur px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-primary shadow-soft">
          <Sparkles className="h-3 w-3" /> A private space for the people who love them
        </div>
        <h1 className="mt-8 font-serif text-6xl sm:text-7xl text-foreground leading-[0.95]">OpenHART</h1>
        <p className="mt-5 font-serif text-xl text-muted-foreground italic">Human Assistance Response Team.</p>
        <p className="mx-auto mt-8 max-w-xl text-base sm:text-lg text-foreground/80 leading-relaxed">
          When someone you love is going through something hard, the noise gets loud and the help
          gets scattered. OpenHART quietly gathers it — the updates, the offers, the little notes —
          into one calm place.
        </p>
      </section>

      {/* HART letters */}
      <section className="mx-auto max-w-5xl px-6 pb-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {letters.map((l, i) => (
            <div
              key={l.letter}
              className="rounded-3xl bg-card shadow-card p-6 transition-gentle hover:shadow-soft"
              style={{ animation: `fade-up 0.6s ease ${i * 0.08}s both` }}
            >
              <div className="font-serif text-5xl text-primary leading-none">{l.letter}</div>
              <div className="mt-3 font-serif text-lg text-foreground">{l.word}</div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{l.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Find your person */}
      <section className="mx-auto max-w-2xl px-6 pb-24">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-[0.25em] text-primary">Who are you here for?</div>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-foreground">Find your person.</h2>
          <p className="mt-3 text-sm text-muted-foreground">Enter the name shared with you by the family.</p>
        </div>
        <form
          onSubmit={handleSearch}
          className="rounded-3xl bg-card shadow-card p-5 sm:p-6 flex flex-col sm:flex-row items-stretch gap-3"
        >
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="e.g. ann-fisher"
              data-testid="find-person-input"
              className="flex w-full px-3 py-2 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 md:text-sm h-12 pl-11 text-base bg-secondary/60 border-0 rounded-2xl"
            />
          </div>
          <button
            type="submit"
            data-testid="find-person-submit"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium bg-gradient-rose text-primary-foreground shadow-soft hover:shadow-glow transition-gentle h-11 rounded-md px-8 sm:w-auto"
          >
            Continue <ArrowRight className="h-4 w-4 ml-1" />
          </button>
        </form>
      </section>

      {/* Trust */}
      <section className="mx-auto max-w-3xl px-6 pb-24">
        <div className="rounded-3xl bg-card/60 backdrop-blur p-6 sm:p-8 shadow-soft">
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            <div>
              <Shield className="h-5 w-5 text-primary mx-auto" />
              <div className="mt-3 font-serif text-base text-foreground">Private by default</div>
              <p className="mt-1 text-xs text-muted-foreground">Passcode-gated. Not indexed. Just for the circle.</p>
            </div>
            <div>
              <MessageCircle className="h-5 w-5 text-primary mx-auto" />
              <div className="mt-3 font-serif text-base text-foreground">One source of truth</div>
              <p className="mt-1 text-xs text-muted-foreground">No more group-text fatigue. Updates land here.</p>
            </div>
            <div>
              <Heart className="h-5 w-5 text-primary mx-auto fill-primary" />
              <div className="mt-3 font-serif text-base text-foreground">Made with care</div>
              <p className="mt-1 text-xs text-muted-foreground">Built around the patient, not the platform.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <div id="pricing">
        <section className="mx-auto max-w-6xl px-6 py-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary">
              <Sparkles className="h-3 w-3" /> Pricing
            </div>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-foreground">
              Simple plans for the people who show up.
            </h2>
            <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto">
              Pick the level of care that fits the moment. Cancel anytime.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {plans.map((plan) => (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative rounded-3xl bg-card p-7 shadow-card transition-gentle hover:shadow-soft flex flex-col ${
                  plan.highlight ? "ring-2 ring-primary shadow-glow" : ""
                } ${plan.id === "whitelabel" ? "bg-gradient-to-br from-card to-primary-soft/30" : ""}`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.2em] px-3 py-1">
                    {plan.badge}
                  </div>
                )}
                {plan.providerBadge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-foreground text-background text-[10px] uppercase tracking-[0.2em] px-3 py-1 inline-flex items-center gap-1">
                    <Building2 className="h-3 w-3" /> {plan.providerBadge}
                  </div>
                )}
                <div className="font-serif text-2xl text-foreground">{plan.name}</div>
                <p className="mt-1 text-xs text-muted-foreground">{plan.tagline}</p>
                <div className="mt-6 flex items-baseline gap-1.5">
                  <span className="font-serif text-5xl text-foreground">{plan.price}</span>
                  <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{plan.period}</span>
                </div>
                <p data-testid={`plan-deployment-fee-${plan.id}`} className="mt-2 text-[11px] italic text-muted-foreground">
                  + One-time deployment fee (determined after consultation)
                </p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-foreground/80">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate(`/start?plan=${plan.id}`)}
                  data-testid={`plan-cta-${plan.id}`}
                  className={`mt-7 inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium transition-gentle ${
                    plan.highlight
                      ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-soft"
                      : plan.id === "whitelabel"
                      ? "bg-foreground text-background hover:bg-foreground/90"
                      : "bg-primary-soft text-primary hover:bg-primary hover:text-primary-foreground"
                  }`}
                >
                  {plan.cta}
                </button>
                <button
                  type="button"
                  onClick={() => setBookingPlan(plan)}
                  data-testid={`plan-book-consultation-${plan.id}`}
                  className="mt-2.5 inline-flex items-center justify-center gap-1 text-xs font-medium text-primary underline-offset-4 hover:underline transition-gentle"
                >
                  Book a consultation
                </button>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-10">Cancel anytime.</p>
          <BookConsultationDialog
            plan={bookingPlan}
            open={!!bookingPlan}
            onOpenChange={(v) => {
              if (!v) setBookingPlan(null);
            }}
          />
        </section>
      </div>
    </main>
  );
};

export default HomePage;
